import { Button, CircularProgress, useSnackbar } from '@packages/shared';
import { TrashOpen32 } from '@packages/themes/icons';
import { defineMessages, useIntl } from 'react-intl';
import { getFragmentData } from '@packages/gql/generated/shopping';
import type {
  LineItemFragmentFragment,
  WishlistLineItemsFragmentFragment,
} from '@packages/gql/generated/shopping/graphql';
import { useAuthMutation } from '@packages/gql';
import type {
  GlycerinCartProduct,
  GlycerinWishListProduct,
  GTMEventGlycerinRemoveFromCart,
  GTMEventGlycerinRemoveFromWishList,
} from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import { WishlistFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistFragmentFragmentDoc';
import type { SxProps } from '@mui/material';
import { buttonStyling, iconStyling } from './Actions.styles';
import { useEventTracking } from '../Tracking/useEventTracking';
import { GraphqlDocuments } from './mappedModeToDocument';

export interface BasketDeleteActionProps {
  mode: 'basket';
  lineItemId: LineItemFragmentFragment['id'];
  lineItemSku?: never;
  glycerinProduct: GlycerinCartProduct;
  overrideButtonSx?: SxProps;
  galleryView?: boolean;
}

export interface WishlistDeleteActionProps {
  mode: 'wishlist';
  lineItemSku: WishlistLineItemsFragmentFragment['sku'];
  lineItemId?: never;
  glycerinProduct: GlycerinWishListProduct;
  overrideButtonSx?: SxProps;
  galleryView?: boolean;
}
export type DeleteActionProps = BasketDeleteActionProps | WishlistDeleteActionProps;
const messages = defineMessages({
  buttonText: {
    id: 'DeleteAction.ButtonText',
    defaultMessage: 'Löschen',
    description: 'this will be displayed on the button',
  },
  successBasketMessage: {
    id: 'DeleteAction.Basket.SuccessMessage',
    defaultMessage: 'Ihr Artikel wurde erfolgreich aus dem Warenkorb entfernt',
  },
  successWishlistMessage: {
    id: 'DeleteAction.Wishlist.SuccessMessage',
    defaultMessage: 'Ihr Artikel wurde erfolgreich von Ihrem Merkzettel entfernt',
  },
});

export const DeleteAction: React.FC<DeleteActionProps> = ({
  mode,
  glycerinProduct,
  lineItemId,
  lineItemSku,
  overrideButtonSx,
  galleryView = false,
}) => {
  const { formatMessage, locale } = useIntl();
  // a current limitation of typescript is that it cannot infer the corect type of the mutation result so useAuthMutation(GraphqlDocuments[mode]) won't work.
  const [basketState, removeFromBasket] = useAuthMutation(GraphqlDocuments.basket);
  const [wishListState, removeFromWishlist] = useAuthMutation(GraphqlDocuments.wishlist);

  const { addToast } = useSnackbar();
  const dispatch = useEventTracking();
  const dispatchGlycerin = useTracking();

  const onClickHandler = () => {
    if (mode === 'basket' && !basketState.fetching && lineItemId) {
      removeFromBasket({ locale, lineItemId })
        .then((result) => {
          const removeFromBasketResult = result.data?.removeFromBasket;
          const basket = getFragmentData(BasketFragmentFragmentDoc, removeFromBasketResult);
          if (basket && basket.success) {
            addToast({
              severity: 'success',
              message: formatMessage(messages.successBasketMessage),
              duration: 5000,
              onClose: () => {},
            });
            dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
              event: 'RemoveFromCart',
              RemoveFromCartData: {
                removedProducts: [glycerinProduct],
              },
            });
            // display cart is fired from basket component due to its rerendering
            dispatch('basket_article', 'delete');
          }
        })
        .catch(() => {
          // no need to grab the error variable currently we aren't displaying it
          addToast({
            severity: 'error',
            message: 'Fehler beim Entfernen des Artikels',
            duration: 5000,
            onClose: () => {},
          });
        });
    }
    if (mode === 'wishlist' && !wishListState.fetching && lineItemSku) {
      removeFromWishlist({ locale, sku: lineItemSku })
        .then((result) => {
          if (result.error) throw new Error('error');
          const removeFromWishlistResult = result.data?.removeFromWishlist;
          const wishlist = getFragmentData(WishlistFragmentFragmentDoc, removeFromWishlistResult);
          if (wishlist) {
            dispatchGlycerin<GTMEventGlycerinRemoveFromWishList>({
              event: 'RemoveFromWishList',
              RemoveFromWishListData: {
                removedProducts: [glycerinProduct],
              },
            });
            addToast({
              severity: 'success',
              message: formatMessage(messages.successWishlistMessage),
              duration: 5000,
              onClose: () => {},
            });
          }
        })
        .catch(() => {
          addToast({
            severity: 'error',
            message: 'Fehler beim Entfernen des Artikels',
            duration: 5000,
            onClose: () => {},
          });
        });
    }
  };
  const stateIcon =
    basketState.fetching || wishListState.fetching ? (
      <CircularProgress sx={iconStyling} size="s" />
    ) : (
      <TrashOpen32 sx={iconStyling} />
    );
  const styles = { ...buttonStyling, ...overrideButtonSx };
  return (
    <Button
      sx={styles}
      onClick={onClickHandler}
      data-testid="delete-action"
      variant="text"
      startIcon={stateIcon}
    >
      {!galleryView && formatMessage(messages.buttonText)}
    </Button>
  );
};
